import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { autorisationsAbsenceAPI } from '../../../services/api';
import { AutorisationAbsence, AutorisationAbsenceValidate, StatutAutorisation } from '../../../types/autorisationAbsence';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';

const HR_COLOR = '#00897B';

interface AutorisationAbsenceValidationDialogProps {
  open: boolean;
  onClose: () => void;
  autorisation: AutorisationAbsence | null;
  onSuccess: () => void;
}

const AutorisationAbsenceValidationDialog: React.FC<AutorisationAbsenceValidationDialogProps> = ({
  open,
  onClose,
  autorisation,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { showSuccess, showError } = useAlert();

  useEffect(() => {
    if (open && autorisation) {
      setCommentaire('');
      setError(null);
    }
  }, [open, autorisation]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const handleValidate = async (statut: StatutAutorisation) => {
    setLoading(true);
    try {
      const data: AutorisationAbsenceValidate = {
        statut,
        commentaire_rh: commentaire || null,
      };
      await autorisationsAbsenceAPI.validate(autorisation!.id, data);
      showSuccess(statut === 'AUTORISEE' ? 'Autorisation accordee avec succes' : 'Demande refusee');
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = extractErrorMessage(err);
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!autorisation) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: HR_COLOR, color: 'white' }}>
        Validation de l'Autorisation d'Absence
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Request Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Details de la demande
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Reference:</Typography>
              <Typography variant="body1" fontWeight="500">{autorisation.ref_autorisation || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Date creation:</Typography>
              <Typography variant="body1">{formatDate(autorisation.created_at)}</Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Employee Info */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Employe
          </Typography>
          <Typography variant="body1" fontWeight="500">
            {autorisation.employee?.nom_complet}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {autorisation.employee?.matricule} - {autorisation.employee?.fonction || 'N/A'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Absence Period */}
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Periode d'absence
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Debut:</Typography>
              <Typography variant="body1">{formatDate(autorisation.date_debut)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Fin:</Typography>
              <Typography variant="body1">{formatDate(autorisation.date_fin)}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">Duree:</Typography>
              <Chip
                label={`${autorisation.nbr_jours} jour(s)`}
                color="primary"
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
            </Grid>
          </Grid>
        </Box>

        {autorisation.motif && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Motif
              </Typography>
              <Typography variant="body2">{autorisation.motif}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Validation Form */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Commentaire RH"
              multiline
              rows={2}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Ajouter un commentaire (optionnel)..."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => handleValidate('REFUSEE')}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Refuser'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleValidate('AUTORISEE')}
          disabled={loading}
          sx={{ bgcolor: '#66BB6A', '&:hover': { bgcolor: '#4CAF50' } }}
        >
          {loading ? <CircularProgress size={24} /> : 'Autoriser'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutorisationAbsenceValidationDialog;
